import {
  IndividualDetailsPost,
  LinkIndividualPostPartial
} from '@interfaces/Individual.interface';
import BaseApiClient from '../BaseApiClient';
import { ListDataParams, ListDataResponse } from '../types';
import {
  CompanyDetailsPOST,
  CompanyInterface
} from '@interfaces/Company.interface';
import { withCompanyListRelationshipIntelligence } from '@/utils/company-relationship-intelligence';
import {
  CompanyRelationshipIntelligenceData,
  PulsePoint,
  PulseTrend,
  RawCompanyRelationshipIntelligencePayload,
  RelationshipActionItem,
  RelationshipHealthState,
  RelationshipInsightItem
} from '@interfaces/RelationshipIntelligence.interface';
import { InsightTypes } from 'stubben-edge-unified-frontend-react-components';
import type ApiClient from '../ApiClient';

export class ApiCompanies extends BaseApiClient {
  private api!: ApiClient;

  setApiClient(api: ApiClient) {
    this.api = api;
  }

  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<CompanyInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList('/companies', listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: withCompanyListRelationshipIntelligence(res.data.data),
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async get(id: number): Promise<CompanyInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/company/${id}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async archive(ids: number[]) {
    return this.client.delete('/company-bulk-delete', {
      data: { ids: ids.map((value: number) => ({ id: value })) }
    });
  }

  async unlinkIndividual(
    individualId: string | number,
    companyId?: string | number
  ) {
    return this.client.delete(
      `/companies/${companyId}/individuals/${individualId}`
    );
  }

  async storeLinkNewIndividual(
    companyId: number | string | undefined,
    dto: IndividualDetailsPost
  ) {
    await this.client.post(`companies/${companyId}/individuals`, dto);
  }

  async updateIndividualPrimaryOrJobTitle(
    companyId: string | undefined,
    individual: number,
    dto: LinkIndividualPostPartial
  ): Promise<void> {
    await this.client.put(
      `companies/${companyId}/individuals/${individual}`,
      dto
    );
  }

  async updateCompanyAddressAndWebsite(
    companyId: string | undefined,
    dto: Pick<CompanyDetailsPOST, 'address' | 'website'>
  ) {
    await this.client.patch(`companies/${companyId}/address`, dto, {
      headers: { Version: '2025-11-14' }
    });
  }

  async getRelationshipIntelligence(
    companyId: string | number
  ): Promise<CompanyRelationshipIntelligenceData> {
    const res = await this.client.get<{
      data: RawCompanyRelationshipIntelligencePayload;
    }>(`/companies/${companyId}/relationship-intelligence`, {
      headers: { Version: '2025-11-14' }
    });

    const raw =
      res.data?.data ??
      (res.data as unknown as RawCompanyRelationshipIntelligencePayload) ??
      ({} as RawCompanyRelationshipIntelligencePayload);

    const rawPoints = raw.sparkline_points ?? [];
    const points: PulsePoint[] = rawPoints.map((score, idx) => ({
      score,
      label: `Interaction ${idx + 1}`
    }));

    const openActionItems: RelationshipActionItem[] = (
      raw.action_items ?? []
    ).map((item) => {
      const meetingId = item.meeting_id ?? item.meetingId ?? undefined;
      const meetingName = item.meeting_name ?? item.meetingName ?? undefined;
      const primaryAction =
        item.primaryAction ?? item.primary_action ?? undefined;
      return {
        id: item.id,
        meeting_id: meetingId,
        meetingId,
        meeting_name: meetingName,
        meetingName,
        title: item.title,
        source: item.source,
        primary_action: primaryAction,
        primaryAction,
        resolved: false
      };
    });

    const resolvedActionItems: RelationshipActionItem[] = (
      raw.resolved_action_items ?? []
    ).map((item) => {
      const meetingId = item.meeting_id ?? item.meetingId ?? undefined;
      const meetingName = item.meeting_name ?? item.meetingName ?? undefined;
      const primaryAction =
        item.primaryAction ?? item.primary_action ?? undefined;
      return {
        id: item.id,
        meeting_id: meetingId,
        meetingId,
        meeting_name: meetingName,
        meetingName,
        title: item.title,
        source: item.source,
        primary_action: primaryAction,
        primaryAction,
        resolved: true
      };
    });

    const insights: RelationshipInsightItem[] = [
      ...(raw.top_positive_aspects ?? []).map((a) => ({
        id: `pos-${a.slug}`,
        type: InsightTypes.BuyingSignal,
        label: a.name,
        description: `Positive momentum score: ${
          (a.score ?? 0) >= 0 ? '+' : ''
        }${Math.round((a.score ?? 0) * 100)}% (Impact factor: ${a.impact ?? 1})`
      })),
      ...(raw.top_friction_aspects ?? []).map((a) => ({
        id: `fric-${a.slug}`,
        type: InsightTypes.ClientConcern,
        label: a.name,
        description: `Friction score: ${Math.round(
          (a.score ?? 0) * 100
        )}% (Impact factor: ${a.impact ?? 1})`
      }))
    ];

    const state = raw.health_state ?? 'fair';
    const trend = raw.trend ?? 'stable';
    const rawScore =
      typeof raw.health_score === 'number' && Number.isFinite(raw.health_score)
        ? raw.health_score
        : 0.5;
    const score = Math.round(rawScore <= 1 ? rawScore * 100 : rawScore);
    const openCount = raw.open_actions_count ?? openActionItems.length;
    const lastTouchAt = raw.last_meeting_at ?? null;

    return {
      company_id: companyId,
      companyId,
      pulse: {
        state,
        healthState: state,
        trend,
        summary: 'Relationship sentiment tracking across interactions.',
        points,
        score,
        open_actions_count: openCount,
        openActionsCount: openCount,
        last_touch_at: lastTouchAt,
        lastTouchAt,
        action_items: openActionItems,
        actionItems: openActionItems,
        resolved_action_items: resolvedActionItems,
        resolvedActionItems: resolvedActionItems
      },
      insights,
      recent_activity: [],
      recentActivity: [],
      action_items: openActionItems,
      actionItems: openActionItems,
      resolved_action_items: resolvedActionItems,
      resolvedActionItems: resolvedActionItems,
      open_actions_count: openCount,
      openActionsCount: openCount,
      last_touch_at: lastTouchAt,
      lastTouchAt
    };
  }
}

export { companiesQueryKeys } from './query-keys';
export type { CompaniesListQueryParams } from './query-keys';
export {
  useCompaniesListQuery,
  useCompanyQuery,
  useCompanyRelationshipIntelligenceQuery
} from './use-companies-queries';
export {
  useCompaniesArchiveMutation,
  useCompaniesStoreLinkNewIndividualMutation,
  useCompaniesUnlinkIndividualMutation,
  useCompaniesUpdateAddressAndWebsiteMutation,
  useCompaniesUpdateLinkedIndividualMutation
} from './use-companies-mutations';
export type {
  CompaniesStoreLinkNewIndividualVariables,
  CompaniesUnlinkIndividualVariables,
  CompaniesUpdateAddressAndWebsiteVariables,
  CompaniesUpdateLinkedIndividualVariables
} from './use-companies-mutations';
